// File: components/Stage.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GroupConfig, ProducerSkills, PlayerProfile, CostumeId } from '../types';
import { GameInstance } from './engine/GameInstance';
import { GameCanvas } from './GameCanvas';
import { HUD } from './HUD';
import { Overlay } from './Overlay';
import { GameStatus, Song } from './types/common';
import { MusicManager } from './audio/GameAudioManager';
import { ExitButton } from './ExitButton';
import { FlaskConical, ShieldAlert, Shirt, Activity, Mic2 } from 'lucide-react';
import { COSTUME_DATA, CITY_VIBES } from '../constants';

interface StageProps {
    playerSquad: GroupConfig;
    playerSkills: ProducerSkills;
    producerName: string;
    tonicCount: number;
    mysticWards: number;
    currentCity: string;
    unlockedCostumes: CostumeId[];
    isPracticeMode?: boolean;
    isPlaneStage?: boolean;
    isBathhouseFight?: boolean;
    isBackgroundOnly?: boolean;
    hideWeapon?: boolean;
    onUseTonic: () => void;
    onUseWard: () => void;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: (winnings: number, won: boolean, gainedSkills?: Partial<ProducerSkills>, enemiesDefeated?: number, achievedGoldenHonmoon?: boolean) => void;
    song: Song;
}

const VIBE_LABELS: Record<string, string> = {
    'TRAP': 'High-Energy',
    'ANTHEM': 'Confident',
    'HOUSE': 'Bubbly',
    'BUILDUP': 'Emotional',
    'MINIMAL': 'Intimate',
};

export const Stage: React.FC<StageProps> = ({
    playerSquad,
    playerSkills,
    producerName,
    tonicCount,
    mysticWards,
    currentCity,
    unlockedCostumes,
    isPracticeMode = false,
    isPlaneStage = false,
    isBathhouseFight = false,
    isBackgroundOnly = false,
    hideWeapon = false,
    onUseTonic,
    onUseWard,
    onUpdatePlayer,
    onExit,
    song
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [gameState, setGameState] = useState<GameStatus>("MENU");
    const [stats, setStats] = useState({ score: 0, honmoon: 100, wave: 1, goldenHonmoonTimer: 0 });
    const [resultScore, setResultScore] = useState(0);

    const [localTonics, setLocalTonics] = useState(tonicCount);
    const [localWards, setLocalWards] = useState(mysticWards);
    const [wardTriggered, setWardTriggered] = useState(false);
    const [vibeStatus, setVibeStatus] = useState<'MATCH' | 'CLASH' | 'NEUTRAL'>('NEUTRAL');
    const [songDebuffActive, setSongDebuffActive] = useState(false);

    const game = useRef<GameInstance | null>(null);

    if (!game.current) {
        game.current = new GameInstance({
            playerSquad,
            producerName,
            currentCity,
            isPlaneStage,
            isBathhouseFight,
            isBackgroundOnly,
            isPracticeMode,
            hideWeapon,
            callbacks: {
                onUpdateUI: (score, honmoon, wave, goldenHonmoonTimer) => setStats({ score, honmoon, wave, goldenHonmoonTimer }),
                onGameOver: (score) => {
                },
                onVictory: (score) => {
                    setResultScore(score);
                    setGameState("VICTORY");
                }
            }
        });

        game.current.callbacks.onGameOver = (score) => {
            if (wardRef.current > 0) {
                triggerWardRevive();
            } else {
                setResultScore(score);
                setGameState("GAMEOVER");
            }
        };
    }

    useEffect(() => {
        if (game.current) {
            game.current.playerSquad = playerSquad;
        }
    }, [playerSquad]);

    useEffect(() => {
        if (game.current) {
            game.current.isBackgroundOnly = isBackgroundOnly;
        }
    }, [isBackgroundOnly]);

    useEffect(() => {
        if (!song || isPracticeMode || isPlaneStage) {
            setVibeStatus('NEUTRAL');
            return;
        }

        const preferredVibes = CITY_VIBES[currentCity] || [];
        const songRhythm = song.structure.find(s => s.type === 'CHORUS')?.rhythm || 'ANTHEM';

        if (preferredVibes.length > 0) {
            if (preferredVibes.includes(songRhythm)) {
                setVibeStatus('MATCH');
            } else {
                setVibeStatus('CLASH');
            }
        } else {
            setVibeStatus('NEUTRAL');
        }
    }, [song, currentCity, isPracticeMode, isPlaneStage]);

    const wardRef = useRef(mysticWards);
    const tonicRef = useRef(tonicCount);

    useEffect(() => {
        wardRef.current = localWards;
        tonicRef.current = localTonics;
    }, [localWards, localTonics]);

    const triggerWardRevive = () => {
        if (!game.current) return;
        setLocalWards(prev => prev - 1);
        onUseWard();

        game.current.honmoon = 50;
        game.current.shake = 20;
        game.current.isGameOver = false;

        setWardTriggered(true);
        setTimeout(() => setWardTriggered(false), 2500);
        setStats(prev => ({ ...prev, honmoon: 50 }));
    };

    const startGame = useCallback(() => {
        game.current?.reset();
        setResultScore(0);
        setGameState("PLAYING");
    }, []);

    useEffect(() => {
        if (isPlaneStage && !isBackgroundOnly) {
            startGame();
        }
    }, [isPlaneStage, isBackgroundOnly, startGame]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent, down: boolean) => {
            if (!game.current) return;
            if (e.code === "ArrowLeft") game.current.keys.ArrowLeft = down;
            if (e.code === "ArrowRight") game.current.keys.ArrowRight = down;
            if (e.code === "ArrowUp") game.current.keys.ArrowUp = down;
            if (e.code === "ArrowDown") game.current.keys.ArrowDown = down;
            if (e.code === "Space") game.current.keys.Space = down;
            if (down && (e.key === 't' || e.key === 'T')) {
                handleUseTonic();
            }
        };
        const down = (e: KeyboardEvent) => onKey(e, true);
        const up = (e: KeyboardEvent) => onKey(e, false);
        window.addEventListener("keydown", down);
        window.addEventListener("keyup", up);
        return () => {
            window.removeEventListener("keydown", down);
            window.removeEventListener("keyup", up);
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !game.current) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        let frameId: number;
        let debuffTimer = 0;

        const render = () => {
            if (gameState === "PLAYING") {
                game.current?.update();

                if (!isPracticeMode && !isPlaneStage) {
                    if (song.id === 'TAKEDOWN') {
                        debuffTimer++;
                        if (debuffTimer > 60) {
                            if (game.current!.goldenHonmoonTimer <= 0) {
                                game.current!.honmoon = Math.max(0, game.current!.honmoon - 0.5);
                                setSongDebuffActive(true);
                            } else {
                                setSongDebuffActive(false);
                            }
                            debuffTimer = 0;
                        }
                    } else {
                        setSongDebuffActive(false);
                    }

                    if (song.id === 'GOLDEN') {
                        if (Math.random() < 0.01) game.current!.score += 5;
                    }
                }

                if (vibeStatus === 'CLASH' && Math.random() < 0.01) {
                    game.current!.honmoon = Math.max(0, game.current!.honmoon - 0.05);
                } else if (vibeStatus === 'MATCH' && Math.random() < 0.01) {
                    game.current!.score += 10;
                }
            }
            game.current?.draw(ctx);
            frameId = requestAnimationFrame(render);
        };
        render();
        return () => cancelAnimationFrame(frameId);
    }, [gameState, vibeStatus, song.id]);

    const handleUseTonic = () => {
        if (tonicRef.current > 0 && game.current && gameState === 'PLAYING') {
            game.current.honmoon = Math.min(100, game.current.honmoon + 25);
            setLocalTonics(prev => prev - 1);
            onUseTonic();
            setStats(prev => ({ ...prev, honmoon: game.current!.honmoon }));
        }
    };

    const handleCycleCostume = () => {
        if (unlockedCostumes.length <= 1) return;

        const currentCostume = playerSquad.members[0].costume;
        const currentIndex = unlockedCostumes.indexOf(currentCostume);
        const nextIndex = (currentIndex + 1) % unlockedCostumes.length;
        const nextCostume = unlockedCostumes[nextIndex];

        const newMembers = playerSquad.members.map(m => ({
            ...m,
            costume: nextCostume
        }));

        onUpdatePlayer({ group: { ...playerSquad, members: newMembers } });
    };

    const handleExit = () => {
        const winnings = Math.floor(resultScore / 10);
        const isWin = gameState === "VICTORY";
        const enemiesDefeated = game.current?.enemiesDefeated || 0;
        const achievedGoldenHonmoon = game.current?.achievedGoldenHonmoonThisRun || false;
        onExit(winnings, isWin, undefined, enemiesDefeated, achievedGoldenHonmoon);
    };

    useEffect(() => {
        if (gameState === 'VICTORY' && isPlaneStage) {
            handleExit();
        }
    }, [gameState, isPlaneStage]);

    const handleRetreat = () => {
        const enemiesDefeated = game.current?.enemiesDefeated || 0;
        const isWinForSkip = isPlaneStage;
        onExit(0, isWinForSkip, undefined, enemiesDefeated, false);
    };

    const currentCostumeLabel = COSTUME_DATA[playerSquad.members[0].costume].label;

    let title = "";
    if (isPlaneStage) title = "PLANE AMBUSH";
    else if (isBathhouseFight) title = "BATHHOUSE RAID";
    else if (isPracticeMode) title = "STAGE REHEARSAL";

    const subtitle = isPracticeMode
        ? "FOR PRACTICE ONLY - NO STAKES"
        : (isBathhouseFight ? "FLUSH OUT THE ENEMY!" : "DEFEND THE HONMOON");

    const startButtonText = isPracticeMode
        ? "START PRACTICE"
        : (isBathhouseFight ? "FIGHT!" : "START SHOW");

    let victoryTitle = "CONCERT SUCCESS!";
    let victoryDesc = "COUCH TIME EARNED!";

    if (isPracticeMode) {
        victoryTitle = "PRACTICE SUCCESS!";
        victoryDesc = "Excellent form! Skill levels will increase.";
    } else if (isBathhouseFight) {
        victoryTitle = "SAJA BOYS DEFEATED!";
        victoryDesc = "You flushed them out! (Jinu says goodbye awkwardly)";
    } else if (isPlaneStage) {
        victoryTitle = "THREAT NEUTRALIZED";
        victoryDesc = "Rumi: 'Okay, let's do this.'";
    }

    const cityVibes = CITY_VIBES[currentCity] || [];
    const cityVibeLabels = cityVibes.map(v => VIBE_LABELS[v] || v).join(" OR ");

    // Determine Exit Button Label
    let exitLabel = "ABORT SHOW";
    if (isPracticeMode) exitLabel = "ABORT PRACTICE";
    else if (isPlaneStage) exitLabel = "SKIP";
    else if (isBathhouseFight) exitLabel = "EXIT";

    return (
        <div className="relative w-full h-full bg-black flex items-center justify-center p-4">

            {(gameState === 'MENU' || gameState === 'PLAYING') && !isBackgroundOnly && (
                <ExitButton
                    onExit={handleRetreat}
                    label={exitLabel}
                    className="z-[500] bg-red-950/90 border-red-500 shadow-xl"
                />
            )}

            <div className="relative w-full max-w-[800px] aspect-[4/3] max-h-full bg-[#111] shadow-[0_0_20px_#ff007f] rounded-lg overflow-hidden border-2 border-slate-700 mx-auto">
                <GameCanvas canvasRef={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />

                {gameState === 'PLAYING' && (
                    <>
                        <HUD
                            score={stats.score}
                            honmoon={stats.honmoon}
                            goldenHonmoonTimer={stats.goldenHonmoonTimer}
                            song={song}
                            isPracticeMode={isPracticeMode}
                        />

                        {songDebuffActive && (
                            <div className="absolute top-20 left-4 px-3 py-1 rounded font-bold text-xs flex items-center gap-2 border bg-red-900/80 text-red-400 border-red-500 animate-pulse">
                                <Mic2 size={14} /> VOCAL STRAIN! (High Dmg / Draining)
                            </div>
                        )}
                        {song.id === 'GOLDEN' && !isPracticeMode && (
                            <div className="absolute top-20 left-4 px-3 py-1 rounded font-bold text-xs flex items-center gap-2 border bg-yellow-900/80 text-yellow-400 border-yellow-500">
                                <Activity size={14} /> GOLDEN RESONANCE (Double Charge)
                            </div>
                        )}

                        {!isPracticeMode && !isPlaneStage && !isBathhouseFight && (
                            <div className={`absolute top-20 right-4 px-3 py-1 rounded font-bold text-xs flex items-center gap-2 border animate-in slide-in-from-right duration-700 ${vibeStatus === 'MATCH' ? 'bg-green-900/80 text-green-400 border-green-500' :
                                vibeStatus === 'CLASH' ? 'bg-red-900/80 text-red-400 border-red-500 animate-pulse' :
                                    'bg-slate-900/50 text-slate-400 border-slate-700'
                                }`}>
                                <Mic2 size={14} />
                                {vibeStatus === 'MATCH' ? "PERFECT VIBE MATCH" : vibeStatus === 'CLASH' ? "VIBE CLASH!" : "CROWD NEUTRAL"}
                            </div>
                        )}

                        {wardTriggered && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50 animate-in fade-in zoom-in duration-300">
                                <div className="bg-amber-900/90 border-4 border-amber-500 p-6 md:p-8 rounded-xl text-center shadow-[0_0_50px_#f59e0b] max-w-[80%]">
                                    <ShieldAlert size={64} className="mx-auto text-amber-400 mb-4 animate-bounce" />
                                    <h2 className="text-2xl md:text-4xl font-heading text-white mb-2">WARD ACTIVATED!</h2>
                                    <div className="mt-4 text-xs md:text-sm text-white bg-black/50 px-4 py-2 rounded">Honmoon Restored to 50%</div>
                                </div>
                            </div>
                        )}

                        <div className="absolute bottom-4 left-4 right-4 pointer-events-auto flex items-end justify-between">

                            <div className="flex flex-col gap-2">
                                <div className="text-[10px] md:text-xs font-bold text-amber-500 flex items-center gap-1 bg-black/50 px-2 py-1 rounded w-fit border border-amber-500/30">
                                    <ShieldAlert size={12} /> {localWards}
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={handleUseTonic}
                                        disabled={localTonics <= 0}
                                        className="flex items-center gap-2 px-3 py-2 bg-purple-900/80 hover:bg-purple-700 border border-purple-500 rounded-lg text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed group text-xs md:text-sm shadow-lg backdrop-blur-sm"
                                    >
                                        <FlaskConical size={16} className={localTonics > 0 ? "text-purple-300 group-hover:rotate-12 transition-transform" : "text-gray-500"} />
                                        <span>TONIC ({localTonics})</span>
                                    </button>

                                    <button
                                        onClick={handleCycleCostume}
                                        disabled={unlockedCostumes.length <= 1}
                                        className="flex items-center gap-2 px-3 py-2 bg-slate-800/80 hover:bg-slate-700 border border-slate-500 rounded-lg text-white font-bold transition-all disabled:opacity-50 group text-xs md:text-sm shadow-lg backdrop-blur-sm"
                                        title="Cycle Costume"
                                    >
                                        <Shirt size={16} className="text-pink-400 group-hover:scale-110 transition-transform" />
                                        <span className="hidden sm:inline uppercase">{currentCostumeLabel}</span>
                                    </button>
                                </div>
                            </div>

                        </div>
                    </>
                )}

                {gameState === "MENU" && !isPlaneStage && (
                    <Overlay
                        title={title}
                        subtitle={subtitle}
                        buttonText={startButtonText}
                        onClick={startGame}
                        titleColor={isPracticeMode ? "#22d3ee" : undefined}
                    >
                        <div className="flex flex-col items-center gap-6 w-full px-8 py-4">
                            <div className="flex flex-col items-center gap-2 w-full">
                                <p className="text-2xl md:text-3xl text-pink-400 font-heading uppercase tracking-widest flex items-center justify-center gap-2 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] w-full text-center">
                                    <Mic2 size={24} /> {song.title}
                                </p>
                                <p className="text-sm md:text-lg text-slate-100 italic text-center leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,1)] w-full max-w-2xl mx-auto px-4">
                                    {song.vibe}
                                </p>
                            </div>

                            {!isPracticeMode && !isBathhouseFight && (
                                <div className="flex flex-col items-center gap-4 w-full">
                                    <div className={`text-xs md:text-sm font-bold px-4 py-1.5 rounded-full border shadow-2xl ${vibeStatus === 'MATCH' ? 'bg-green-600/80 text-white border-green-400' :
                                        vibeStatus === 'CLASH' ? 'bg-red-600/80 text-white border-red-400' :
                                            'bg-slate-700/80 text-slate-200 border-slate-500'
                                        }`}>
                                        {vibeStatus === 'MATCH' ? "PERFECT VIBE MATCH" : vibeStatus === 'CLASH' ? "VIBE CLASH DETECTED" : "NEUTRAL CROWD"}
                                    </div>

                                    <div className="text-lg md:text-xl text-yellow-400 font-black uppercase drop-shadow-[0_4px_4px_rgba(0,0,0,1)] bg-black/80 px-6 py-3 rounded-xl border border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)] tracking-wide text-center">
                                        Crowd Wants: {cityVibeLabels}
                                    </div>
                                </div>
                            )}

                            <div className="text-center pointer-events-none mt-4">
                                <p className="hidden md:block mb-2 text-slate-300 text-base font-bold bg-black/60 inline-block px-6 py-2 rounded border border-slate-600 shadow-lg">
                                    Use ARROW KEYS to move. SPACE to deflect insults.
                                </p>
                                <p className="md:hidden text-xs mb-2 bg-black/40 inline-block px-4 py-1 rounded">
                                    Tap sides to move. Tap center to attack.
                                </p>
                            </div>
                        </div>
                    </Overlay>
                )}
                {gameState === "GAMEOVER" && (
                    <Overlay title={isPlaneStage ? "PLANE OVERWHELMED" : (isPracticeMode ? "REHEARSAL ENDED" : (isBathhouseFight ? "RETREAT!" : "COUCH TIME RUINED"))} subtitle={`FINAL HYPE: ${resultScore}`} buttonText="RETRY" onClick={isPlaneStage ? startGame : handleExit} titleColor="red">
                        <p>{isPlaneStage ? "The possessed staff overwhelmed the group." : (isPracticeMode ? "Practice session completed." : "Zoey: 'THEY WILL FEEL MY WRATH!'")}</p>
                    </Overlay>
                )}
                {gameState === "VICTORY" && !isPlaneStage && (
                    <Overlay
                        title={victoryTitle}
                        subtitle={`FINAL HYPE: ${resultScore}`}
                        buttonText={isPracticeMode ? "FINISH PRACTICE" : (isBathhouseFight ? "MISSION COMPLETE" : "FINISH PERFORMANCE")}
                        onClick={handleExit}
                        titleColor={isPracticeMode ? "#22d3ee" : (isBathhouseFight ? "#facc15" : "#00ff9d")}
                    >
                        <p>{victoryDesc}</p>
                    </Overlay>
                )}
            </div>
        </div>
    );
};